<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯服務條款' : '新增服務條款' }}</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleCancel">取消</button>
        <button type="button" class="btn btn-primary" :disabled="submitting" @click="handleSubmit">
          <span v-if="submitting"><i class="ri-loader-4-line spin"></i> {{ isEdit ? '更新中...' : '建立中...' }}</span>
          <span v-else>{{ isEdit ? '更新' : '建立' }}</span>
        </button>
      </div>
    </div>

    <div class="page-container">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <p class="error-message">{{ error }}</p>
        <button class="btn btn-secondary" @click="handleCancel">返回</button>
      </div>

      <div v-else class="form-card">
        <form @submit.prevent="handleSubmit">
          <div class="form-row">
            <div class="form-group">
              <label class="required">版本號</label>
              <input
                v-model="form.version"
                type="text"
                class="form-control"
                placeholder="例如：1.0.0"
                required
                maxlength="20"
              />
              <small class="form-text">版本號必須唯一</small>
            </div>

            <div class="form-group">
              <label class="required">語言</label>
              <select v-model="form.language" class="form-control" required>
                <option value="zh-TW">繁體中文</option>
                <option value="zh-CN">簡體中文</option>
                <option value="en">English</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group full-width">
              <label class="required">標題</label>
              <input
                v-model="form.title"
                type="text"
                class="form-control"
                placeholder="請輸入標題"
                required
                maxlength="255"
              />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group full-width">
              <label class="required">內容</label>
              <textarea
                v-model="form.content"
                class="form-control"
                rows="15"
                placeholder="請輸入服務條款內容"
                required
              ></textarea>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label class="required">生效日期</label>
              <input
                v-model="form.effectiveDate"
                type="date"
                class="form-control"
                required
              />
            </div>

            <div class="form-group">
              <label>狀態</label>
              <select v-model="form.status" class="form-control">
                <option value="draft">草稿</option>
                <option value="published">已發布</option>
                <option value="archived">已歸檔</option>
              </select>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { termsService } from '@/api/modules/terms.js';
import { showSuccess, showError } from '@/utils/swal.js';

export default {
  name: 'CreateTerms',
  setup() {
    const router = useRouter();
    const route = useRoute();
    const submitting = ref(false);
    const loading = ref(false);
    const error = ref(null);

    const isEdit = computed(() => !!route.params.id);

    const form = ref({
      version: '',
      title: '',
      content: '',
      language: 'zh-TW',
      status: 'draft',
      effectiveDate: ''
    });

    const fetchTerm = async () => {
      if (!isEdit.value) return;

      loading.value = true;
      error.value = null;

      try {
        const response = await termsService.getTerm(route.params.id);
        if (response && response.success && response.data) {
          const data = response.data;
          form.value = {
            version: data.version || '',
            title: data.title || '',
            content: data.content || '',
            language: data.language || 'zh-TW',
            status: data.status || 'draft',
            effectiveDate: data.effectiveDate ? data.effectiveDate.split('T')[0] : ''
          };
        } else {
          error.value = '獲取服務條款失敗';
        }
      } catch (err) {
        console.error('獲取服務條款失敗:', err);
        error.value = err.message || '獲取服務條款失敗';
      } finally {
        loading.value = false;
      }
    };

    const handleSubmit = async () => {
      submitting.value = true;
      error.value = null;

      try {
        if (isEdit.value) {
          const response = await termsService.updateTerm(route.params.id, form.value);
          if (response && response.success) {
            await showSuccess('更新成功', '服務條款已成功更新');
            router.push('/client/terms');
          } else {
            await showError('更新失敗', response?.message || '更新失敗');
          }
        } else {
          const response = await termsService.createTerm(form.value);
          if (response && response.success) {
            await showSuccess('建立成功', '服務條款已成功建立');
            router.push('/client/terms');
          } else {
            await showError('建立失敗', response?.message || '建立失敗');
          }
        }
      } catch (err) {
        console.error('提交失敗:', err);
        await showError('操作失敗', err.message || '操作失敗，請稍後再試');
      } finally {
        submitting.value = false;
      }
    };

    const handleCancel = () => {
      router.push('/client/terms');
    };

    onMounted(() => {
      fetchTerm();
    });

    return {
      form,
      isEdit,
      submitting,
      loading,
      error,
      handleSubmit,
      handleCancel,
    };
  },
};
</script>

<style scoped>
.page-outer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

.page-header {
  background: #f5f7fa;
  border-bottom: 1px solid #e5e7eb;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.header-left { display: flex; align-items: center; }
.header-actions { display: flex; align-items: center; gap: 8px; }
.page-title { font-size: 20px; font-weight: 600; margin: 0; }

.page-container {
  padding: 20px;
  padding-top: 72px;
}

.form-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 24px;
}

.form-row {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
}

.form-row:last-child { margin-bottom: 0; }

.form-group { flex: 1; }
.form-group.full-width { flex: 1 1 100%; }

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 6px;
}

.form-group label.required::after { content: ' *'; color: #ef4444; }

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

textarea.form-control { resize: vertical; font-family: inherit; line-height: 1.6; }

.form-text { display: block; margin-top: 4px; font-size: 12px; color: #9ca3af; }

.loading-state, .error-state { text-align: center; padding: 60px 20px; }
.error-message { color: #ef4444; margin-bottom: 16px; }

.btn { padding: 6px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; border: none; display: inline-flex; align-items: center; gap: 6px; }
.btn-primary { background: #3b82f6; color: #fff; }
.btn-primary:hover:not(:disabled) { background: #2563eb; }
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-secondary { background: #f3f4f6; color: #374151; border: 1px solid #e5e7eb; }
.btn-secondary:hover { background: #e5e7eb; }

.spin { animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>
